import api from '@/api';

const types = {
  SET_QUIZ_SESSION: 'SET_QUIZ_SESSION',
  SET_SESSION_QUESTIONS: 'SET_SESSION_QUESTIONS',
  ANSWER_QUESTION: 'ANSWER_QUESTION',
  BOOKMARK_QUESTION: 'BOOKMARK_QUESTION',
  SET_COUNTDOWN_SCHEDULE: 'SET_COUNTDOWN_SCHEDULE',
  SET_ACTIVE_QUESTION: 'SET_ACTIVE_QUESTION',
  SET_ACTIVE_QUESTION_BY_NUMBER: 'SET_ACTIVE_QUESTION_BY_NUMBER',
  SET_CURRENT_STATUS_DRAWER_STATE: 'SET_CURRENT_STATUS_DRAWER_STATE',
  SET_OVERVIEW_DRAWER_STATE: 'SET_OVERVIEW_DRAWER_STATE',
  SET_COMPLETE_SESSION_MODAL_STATE: 'SET_COMPLETE_SESSION_MODAL_STATE',
  SET_RESULTS: 'SET_RESULTS',
  SET_QUESTION_DETAIL_MODAL_STATE: 'SET_QUESTION_DETAIL_MODAL_STATE',
  SET_RESULT_DETAIL_ITEM: 'SET_RESULT_DETAIL_ITEM',
};

const state = {
  quizSession: null,
  sessionQuestions: null,
  countdownSchedule: null,
  drawer: {
    currentStatus: false,
    overview: false,
  },
  modal: {
    completeSession: false,
    questionDetail: false,
  },
  results: null,
  resultDetailItem: null,
};

const getters = {
  getQuizSession: (state) => state.quizSession,
  getSessionQuestions: (state) => state.sessionQuestions,
  getCountdownSchedule: (state) => state.countdownSchedule,
  getActiveSessionQuestion: (state) => (state.sessionQuestions ? state.sessionQuestions.find((question) => question.isActive) : null),
  getCurrentStatusDrawerState: (state) => state.drawer.currentStatus,
  getOverviewDrawerState: (state) => state.drawer.overview,
  getCompleteSessionModalState: (state) => state.modal.completeSession,
  getResults: (state) => state.results,
  getQuestionDetailModalState: (state) => state.modal.questionDetail,
  getResultDetailItem: (state) => state.resultDetailItem,
};

const mutations = {
  [types.SET_QUIZ_SESSION](state, quizSession) {
    state.quizSession = quizSession;
  },
  [types.SET_SESSION_QUESTIONS](state, questions) {
    state.sessionQuestions = questions;
  },
  [types.ANSWER_QUESTION](state, payload) {
    const { userAnswer, isNull, activeQuestion } = payload;

    state.sessionQuestions = state.sessionQuestions.map((item) => {
      if (item.id === activeQuestion.id) {
        // eslint-disable-next-line no-param-reassign
        item.user_answer = isNull ? null : userAnswer;

        // eslint-disable-next-line no-param-reassign
        item.answers = item.answers.map((answer) => {
        // eslint-disable-next-line no-param-reassign
          answer.isSelected = answer.id === userAnswer.id && !isNull;

          return {
            ...answer,
          };
        });
      }
      return {
        ...item,
      };
    });
  },
  [types.BOOKMARK_QUESTION](state, question) {
    const { id } = question;
    state.sessionQuestions = state.sessionQuestions.map((item) => {
      if (item.id === id) {
        // eslint-disable-next-line no-param-reassign
        item.is_bookmarked = !item.is_bookmarked;
      }

      return {
        ...item,
      };
    });
  },
  [types.SET_COUNTDOWN_SCHEDULE](state, value) {
    state.countdownSchedule = value;
  },
  [types.SET_ACTIVE_QUESTION](state, direction) {
    const { sessionQuestions } = state;
    const currentIndex = sessionQuestions.findIndex((item) => item.isActive);
    const lastIndex = sessionQuestions.length - 1;

    if (direction === 'next') {
      if (currentIndex < lastIndex) {
        sessionQuestions.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.isActive = false;
        });
        sessionQuestions[currentIndex + 1].isActive = true;
      }
    } else if (direction === 'prev') {
      if (currentIndex > 0) {
        sessionQuestions.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.isActive = false;
        });
        sessionQuestions[currentIndex - 1].isActive = true;
      }
    }
  },
  [types.SET_ACTIVE_QUESTION_BY_NUMBER](state, number) {
    const { sessionQuestions } = state;
    const lastIndex = sessionQuestions.length - 1;

    if (number >= 0 && number - 1 <= lastIndex) {
      sessionQuestions.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.isActive = false;
      });
      sessionQuestions[number - 1].isActive = true;
      state.drawer.overview = false;
    }
  },
  [types.SET_CURRENT_STATUS_DRAWER_STATE](state, value) {
    state.drawer.currentStatus = value;
  },
  [types.SET_OVERVIEW_DRAWER_STATE](state, value) {
    state.drawer.overview = value;
  },
  [types.SET_COMPLETE_SESSION_MODAL_STATE](state, value) {
    state.modal.completeSession = value;
  },
  [types.SET_RESULTS](state, value) {
    state.results = value;
  },
  [types.SET_QUESTION_DETAIL_MODAL_STATE](state, value) {
    state.modal.questionDetail = value;
  },
  [types.SET_RESULT_DETAIL_ITEM](state, item) {
    state.resultDetailItem = item;
  },
};

const actions = {
  async checkSession() {
    const response = await api.checkActiveSessions();
    return response;
  },
  async getQuizSession({ commit }) {
    const response = await api.getQuizSession();
    if (response && response.status === 200) {
      const { data: { questions: rawQuestions } } = response;

      // Add `isActive` key to all questions and `isSelected` key to all answers.
      const questions = rawQuestions.map((item, index) => {
        // eslint-disable-next-line no-param-reassign
        item.answers = item.answers.map((answer) => ({
          ...answer,
          isSelected: (!!item.user_answer) && item.user_answer.id === answer.id,
        }));

        return {
          ...item,
          isActive: index === 0,
        };
      });

      commit(types.SET_SESSION_QUESTIONS, questions);
      commit(types.SET_QUIZ_SESSION, response.data);
    }
    return response;
  },
  async createQuizSession(commit, { quizId }) {
    const params = {
      quiz: quizId,
    };

    const response = await api.createQuizSession(params);

    return response;
  },
  async startQuizSession() {
    const response = await api.startQuizSession();
    return response;
  },
  async completeQuizSession({ commit }) {
    const response = await api.completeQuizSession();
    if (response && response.status === 204) {
      commit(types.SET_COUNTDOWN_SCHEDULE, null);
    }
    return response;
  },
  async answerQuestion(commit, { sessionQuestionId, answerId }) {
    const response = await api.answerQuestion(sessionQuestionId, answerId);
    return response;
  },
  async bookmarkQuestion({ commit }, { question }) {
    const { id } = question;
    const response = await api.toggleQuestionBookmark(id);

    if (response && response.status === 204) {
      commit(types.BOOKMARK_QUESTION, question);
    }

    return response;
  },
  async getResults({ commit }) {
    const response = await api.getLastSessionResults();
    if (response && response.status === 200) {
      commit(types.SET_RESULTS, response.data);
    }
    return response;
  },
  updateSessionQuestions({ commit, getters }, { answer, isNull }) {
    const { getActiveSessionQuestion } = getters;

    const payload = {
      userAnswer: answer,
      isNull,
      activeQuestion: getActiveSessionQuestion,
    };
    commit(types.ANSWER_QUESTION, payload);
  },
  setCountDownSchedule({ commit }, { value }) {
    commit(types.SET_COUNTDOWN_SCHEDULE, value);
  },
  navigateQuestion({ commit }, { direction }) {
    commit(types.SET_ACTIVE_QUESTION, direction);
  },
  setActiveQuestionByIndex({ commit }, { number }) {
    commit(types.SET_ACTIVE_QUESTION_BY_NUMBER, number);
  },
  setCurrentStatusDrawerState({ commit }, value) {
    commit(types.SET_CURRENT_STATUS_DRAWER_STATE, value);
  },
  setOverviewDrawerState({ commit }, value) {
    commit(types.SET_OVERVIEW_DRAWER_STATE, value);
  },
  setCompleteSessionModalState({ commit }, value) {
    commit(types.SET_COMPLETE_SESSION_MODAL_STATE, value);
  },
  setQuestionDetailModalState({ commit }, value) {
    commit(types.SET_QUESTION_DETAIL_MODAL_STATE, value);
  },
  setResultDetailItem({ commit }, item) {
    commit(types.SET_RESULT_DETAIL_ITEM, item);
  },
  resetSessionState({ commit }) {
    commit(types.SET_COUNTDOWN_SCHEDULE, null);
    commit(types.SET_SESSION_QUESTIONS, null);
    commit(types.SET_CURRENT_STATUS_DRAWER_STATE, false);
    commit(types.SET_OVERVIEW_DRAWER_STATE, false);
    commit(types.SET_COMPLETE_SESSION_MODAL_STATE, false);
    commit(types.SET_QUESTION_DETAIL_MODAL_STATE, false);
    commit(types.SET_RESULT_DETAIL_ITEM, null);
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
